import React, { useState, useEffect } from 'react';
import supportApiService from '../../services/supportApiService';
import './SupportVirtualAccount.css';

const SupportVirtualAccount = () => {
  // State for wallet operations
  const [findIdentifier, setFindIdentifier] = useState('');
  const [amountToAdd, setAmountToAdd] = useState('');
  const [walletPreview, setWalletPreview] = useState(null);
  const [showWalletPreview, setShowWalletPreview] = useState(false);
  
  // State for create account
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [createAgentIdentifier, setCreateAgentIdentifier] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showSelectedAgent, setShowSelectedAgent] = useState(false);
  
  // Form data for virtual account creation
  const [formData, setFormData] = useState({
    agentId: '',
    accountNumber: '',
    reenterAccountNumber: '',
    ifscCode: ''
  });
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [accountMatchError, setAccountMatchError] = useState(false);

  // Load page data on component mount
  useEffect(() => {
    loadVirtualAccountPage();
  }, []);

  const loadVirtualAccountPage = async () => {
    try {
      setLoading(true);
      const response = await supportApiService.getVirtualAccountPage();
      console.log('🏨 Virtual Account page loaded:', response);
    } catch (error) {
      console.error('❌ Error loading virtual account page:', error);
      showMessage('Failed to load virtual account page', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  // Find wallet by identifier
  const handleFindWallet = async () => {
    if (!findIdentifier.trim()) {
      showMessage('Enter account no / agent id / email', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await supportApiService.findWallet(findIdentifier);
      
      if (response.success && response.wallet) {
        setWalletPreview(response.wallet);
        setShowWalletPreview(true);
        showMessage('Wallet found successfully', 'success');
      } else {
        showMessage(response.message || 'Wallet not found', 'error');
        setShowWalletPreview(false);
      }
    } catch (error) {
      console.error('❌ Error finding wallet:', error);
      showMessage(`Wallet not found for: ${findIdentifier}`, 'error');
      setShowWalletPreview(false);
    } finally {
      setLoading(false);
    }
  };

  // Add amount to wallet
  const handleAddAmount = async () => {
    if (!findIdentifier.trim()) {
      showMessage('Enter identifier', 'error');
      return;
    }

    const amount = parseFloat(amountToAdd);
    if (!amount || amount <= 0) {
      showMessage('Enter a valid amount', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await supportApiService.addAmountToWallet(findIdentifier, amount);
      
      if (response.success) {
        showMessage(`Amount added. New balance: ${response.newBalance}`, 'success');
        setAmountToAdd('');
        // Refresh wallet preview
        if (showWalletPreview) {
          handleFindWallet();
        }
      } else {
        showMessage(response.message || 'Failed to add amount', 'error');
      }
    } catch (error) {
      console.error('❌ Error adding amount:', error);
      showMessage(error.message || 'Error adding amount', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle agent identifier lookup for create form
  const handleAgentLookup = async (identifier) => {
    if (!identifier || identifier.length < 3) return;

    try {
      const response = await supportApiService.getAgentByIdentifier(identifier);
      
      if (response.success && response.agent) {
        setSelectedAgent(response.agent);
        setShowSelectedAgent(true);
        // Prefill create identifier with canonical agentId
        setCreateAgentIdentifier(response.agent.agentId);
        setFormData(prev => ({ ...prev, agentId: response.agent.agentId }));
        showMessage('Agent found successfully', 'success');
      } else {
        showMessage(`Agent not found for: ${identifier}`, 'error');
        setShowSelectedAgent(false);
      }
    } catch (error) {
      console.error('❌ Error finding agent:', error);
      showMessage(`Agent not found for: ${identifier}`, 'error');
      setShowSelectedAgent(false);
    }
  };

  // Handle Enter key press for agent lookup
  const handleAgentIdentifierKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAgentLookup(createAgentIdentifier);
    }
  };

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Check account number match
    if (name === 'reenterAccountNumber' || name === 'accountNumber') {
      const accountNumber = name === 'accountNumber' ? value : formData.accountNumber;
      const reenterAccountNumber = name === 'reenterAccountNumber' ? value : formData.reenterAccountNumber;
      
      if (accountNumber && reenterAccountNumber) {
        setAccountMatchError(accountNumber !== reenterAccountNumber);
      }
    }
  };

  // Handle virtual account creation
  const handleCreateVirtualAccount = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.agentId.trim()) {
      showMessage('Agent ID is required', 'error');
      return;
    }

    if (!formData.accountNumber.trim()) {
      showMessage('Account number is required', 'error');
      return;
    }

    if (!formData.reenterAccountNumber.trim()) {
      showMessage('Please re-enter account number', 'error');
      return;
    }

    if (!formData.ifscCode.trim()) {
      showMessage('IFSC code is required', 'error');
      return;
    }

    if (formData.accountNumber !== formData.reenterAccountNumber) {
      showMessage('Account numbers do not match', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await supportApiService.createVirtualAccount(formData);
      
      if (response.success) {
        showMessage(response.message || 'Virtual account created successfully', 'success');
        // Reset form
        setFormData({
          agentId: '',
          accountNumber: '',
          reenterAccountNumber: '',
          ifscCode: ''
        });
        setCreateAgentIdentifier('');
        setSelectedAgent(null);
        setShowSelectedAgent(false);
        setShowCreateAccount(false);
      } else {
        showMessage(response.message || 'Failed to create virtual account', 'error');
      }
    } catch (error) {
      console.error('❌ Error creating virtual account:', error);
      if (error.message.includes('409')) {
        showMessage('Virtual account already exists for this agent/email/account number', 'error');
      } else {
        showMessage(error.message || 'Failed to create virtual account', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollToCreateSection = () => {
    setShowCreateAccount(true);
    setTimeout(() => {
      const element = document.getElementById('createAccountSection');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <div className="virtual-account-container">
      <div className="container-xl px-4">
        {/* Page Header */}
        <div className="page-header text-center">
          <h1>
            <svg className="icon me-3" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            Virtual Account Activity
          </h1>
          <p className="subtitle">Manage virtual accounts and wallet operations</p>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`alert-custom ${
            message.type === 'error' ? 'alert-danger-custom' : 
            message.type === 'success' ? 'alert-success-custom' : 'alert-info-custom'
          }`}>
            <div className="d-flex justify-content-between align-items-center">
              <span>{message.text}</span>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setMessage({ text: '', type: '' })}
                style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Action Cards */}
        <div className="action-cards">
          {/* Add Amount Card */}
          <div className="action-card">
            <div className="card-header-custom">
              <h3>
                <svg className="icon" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                </svg>
                Add Amount to Wallet
              </h3>
            </div>
            <div className="card-body-custom">
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="input-group-custom mb-4">
                  <div className="form-group-custom">
                    <label className="form-label-custom">Search Wallet</label>
                    <input 
                      type="text"
                      className="form-control form-control-custom" 
                      placeholder="Enter account no. / agent id / email"
                      value={findIdentifier}
                      onChange={(e) => setFindIdentifier(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group-custom">
                    <label className="form-label-custom">Amount</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      className="form-control form-control-custom" 
                      placeholder="Enter amount"
                      value={amountToAdd}
                      onChange={(e) => setAmountToAdd(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="d-flex gap-3">
                  <button 
                    type="button"
                    className="btn btn-custom btn-secondary-custom"
                    onClick={handleFindWallet}
                    disabled={loading}
                  >
                    {loading ? 'Finding...' : 'Find Wallet'}
                  </button>
                  <button 
                    type="button"
                    className="btn btn-custom btn-primary-custom"
                    onClick={handleAddAmount}
                    disabled={loading || !showWalletPreview}
                  >
                    {loading ? 'Adding...' : 'Add Amount'}
                  </button>
                </div>
              </form>

              {/* Wallet Preview */}
              {showWalletPreview && walletPreview && (
                <div className="wallet-preview">
                  <h5>
                    <svg className="icon me-2" viewBox="0 0 24 24">
                      <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                    </svg>
                    {walletPreview.agentName || walletPreview.agentId || 'Agent Wallet'}
                  </h5>
                  <div className="wallet-info">
                    <div className="wallet-info-item">
                      <strong>Agent ID</strong>
                      <span>{walletPreview.agentId || 'N/A'}</span>
                    </div>
                    <div className="wallet-info-item">
                      <strong>Email</strong>
                      <span>{walletPreview.agentEmail || 'N/A'}</span>
                    </div>
                    <div className="wallet-info-item">
                      <strong>Account No.</strong>
                      <span>{walletPreview.virtualAccountDetails?.accountNumber || 'N/A'}</span>
                    </div>
                    <div className="wallet-info-item">
                      <strong>IFSC Code</strong>
                      <span>{walletPreview.virtualAccountDetails?.ifscCode || 'N/A'}</span>
                    </div>
                    <div className="wallet-info-item balance-highlight">
                      <strong>Current Balance</strong>
                      <span>₹{walletPreview.totalBalance || 0}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Create Account Card */}
          <div className="action-card">
            <div className="card-header-custom">
              <h3>
                <svg className="icon" viewBox="0 0 24 24">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
                Create Virtual Account
              </h3>
            </div>
            <div className="card-body-custom">
              {!showCreateAccount ? (
                <div className="feature-highlight">
                  <h4>Create New Virtual Account</h4>
                  <p>Set up a new virtual account for agents to manage their wallet operations</p>
                  <button 
                    className="btn btn-custom btn-primary-custom"
                    onClick={scrollToCreateSection}
                    disabled={loading}
                  >
                    Get Started
                  </button>
                </div>
              ) : (
                <form onSubmit={handleCreateVirtualAccount}>
                  <div className="form-group-custom">
                    <label htmlFor="createAgentIdentifier" className="form-label-custom">
                      Agent ID or Email
                    </label>
                    <input 
                      type="text"
                      id="createAgentIdentifier"
                      className="form-control form-control-custom" 
                      placeholder="Enter agent id or email (Press Enter to search)"
                      value={createAgentIdentifier}
                      onChange={(e) => setCreateAgentIdentifier(e.target.value)}
                      onKeyPress={handleAgentIdentifierKeyPress}
                      disabled={loading}
                      required
                    />
                    <small className="text-muted">Use Agent ID or email only. Press Enter to search.</small>
                  </div>

                  {/* Selected Agent Preview */}
                  {showSelectedAgent && selectedAgent && (
                    <div className="agent-preview">
                      <h5>
                        <svg className="icon me-2" viewBox="0 0 24 24">
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                        </svg>
                        {selectedAgent.firstName} {selectedAgent.lastName || ''}
                      </h5>
                      <div className="agent-info">
                        <div className="agent-info-item">
                          <strong>Agent ID</strong>
                          <span>{selectedAgent.agentId}</span>
                        </div>
                        <div className="agent-info-item">
                          <strong>Email</strong>
                          <span>{selectedAgent.email}</span>
                        </div>
                        <div className="agent-info-item">
                          <strong>Business</strong>
                          <span>{selectedAgent.businessName || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-group-custom">
                    <label htmlFor="accountNumber" className="form-label-custom">Account Number</label>
                    <input 
                      type="text" 
                      className="form-control form-control-custom" 
                      id="accountNumber" 
                      name="accountNumber"
                      value={formData.accountNumber}
                      onChange={handleFormChange}
                      pattern="[0-9]{9,18}" 
                      title="Account number must be between 9 and 18 digits"
                      placeholder="Enter 9-18 digit account number"
                      disabled={loading}
                      required
                    />
                  </div>

                  <div className="form-group-custom">
                    <label htmlFor="reenterAccountNumber" className="form-label-custom">Re-enter Account Number</label>
                    <input 
                      type="text" 
                      className={`form-control form-control-custom ${accountMatchError ? 'form-control-error' : formData.reenterAccountNumber && !accountMatchError ? 'form-control-success' : ''}`}
                      id="reenterAccountNumber" 
                      name="reenterAccountNumber"
                      value={formData.reenterAccountNumber}
                      onChange={handleFormChange}
                      pattern="[0-9]{9,18}" 
                      placeholder="Re-enter account number for confirmation"
                      disabled={loading}
                      required
                    />
                    {accountMatchError && (
                      <div className="validation-error">
                        <svg className="icon" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                        Account numbers do not match
                      </div>
                    )}
                    {formData.reenterAccountNumber && !accountMatchError && (
                      <div className="validation-success">
                        <svg className="icon" viewBox="0 0 24 24">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                        Account numbers match
                      </div>
                    )}
                  </div>

                  <div className="form-group-custom">
                    <label htmlFor="ifscCode" className="form-label-custom">IFSC Code</label>
                    <input 
                      type="text" 
                      className="form-control form-control-custom" 
                      id="ifscCode" 
                      name="ifscCode"
                      value={formData.ifscCode}
                      onChange={handleFormChange}
                      pattern="^[A-Z]{4}0[A-Z0-9]{6}$"
                      title="Enter valid IFSC code (e.g., HDFC0123456)"
                      placeholder="e.g., HDFC0123456"
                      disabled={loading}
                      required
                    />
                  </div>

                  <div className="d-flex gap-3">
                    <button 
                      type="button"
                      className="btn btn-custom btn-outline-custom"
                      onClick={() => setShowCreateAccount(false)}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-custom btn-primary-custom"
                      disabled={loading || accountMatchError}
                    >
                      {loading ? 'Creating Account...' : 'Create Virtual Account'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Loading Spinner */}
        {loading && (
          <div className="loading-spinner">
            <div className="spinner-custom"></div>
          </div>
        )}
      </div>

      {/* Floating Action Button */}
      {!showCreateAccount && (
        <div className="floating-action">
          <button 
            className="fab"
            onClick={scrollToCreateSection}
            title="Create New Virtual Account"
          >
            +
          </button>
        </div>
      )}
    </div>
  );
};

export default SupportVirtualAccount;